import { INotificationMessage } from "@fl/shared/src/types/messages";
import { IUserLogin } from "@fl/shared/src/types/usuarios";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/auth";
import { loginSchema } from "../../schemas/users";
import ApiUsers from "../../services/suporte/users";
import StorageItems from "../../storage";
import { generateMessageReturn } from "../../utils/messageFuncs";
import DefaultPublic from "../default/base";

export type LoginFormInputs = IUserLogin;

const LoginPage = () => {
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<INotificationMessage[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuthToken } = useAuth();

  useEffect(() => {
    if (
      location.state &&
      location.state.message &&
      location.state.message.length > 0
    )
      setMessages(location.state.message);
  }, [location]);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<LoginFormInputs>({
    resolver: yupResolver(loginSchema),
  });

  const onSubmit: SubmitHandler<LoginFormInputs> = async (data) => {
    try {
      setLoading(true);
      const result = await new ApiUsers(data).login();

      if (result.success) {
        if (result.data.avatar)
          new StorageItems().set("avatar", result.data.avatar);
        new StorageItems().set("menus", result.data.menus);
        setAuthToken(result.data.auth);
        navigate("", { state: result });
      } else {
        if (result.message) setMessages(result.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const createUser = () => {
    navigate("/criar-usuario");
  };

  const activeUser = async () => {
    try {
      setLoading(true);
      const email = getValues("email");
      if (!email) {
        setMessages([
          generateMessageReturn(
            "Informe seu e-mail",
            "Para gerar um novo código de confirmação, informe seu e-mail",
            "warning"
          ),
        ]);
      } else {
        const result = await new ApiUsers({ email }).resetCode();
        navigate("", { state: result });
      }
    } finally {
      setLoading(false);
    }
  };

  const resendPassword = async () => {
    try {
      setLoading(true);
      const email = getValues("email");
      if (!email) {
        setMessages([
          generateMessageReturn(
            "Informe seu e-mail",
            "Para gerar o link de recuperação de senha, informe seu e-mail",
            "warning"
          ),
        ]);
      } else {
        const result = await new ApiUsers({ email }).resendPassword();
        navigate("", { state: result });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <DefaultPublic title="Login" message={messages} loading={loading}>
      <div className="d-flex justify-content-center">
        <Form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Form.Group controlId="formId">
            <Form.Label>E-mail:</Form.Label>
            <Form.Control
              type="email"
              placeholder="E-mail do usuário"
              {...register("email")}
              isInvalid={!!errors.email}
            />
            <Form.Control.Feedback type="invalid">
              {errors.email?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="formCode" className="mt-3">
            <Form.Label>Senha:</Form.Label>
            <Form.Control
              type="password"
              placeholder="Senha do usuário"
              {...register("senha")}
              isInvalid={!!errors.senha}
            />
            <Form.Control.Feedback type="invalid">
              {errors.senha?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <div className="d-grid gap-2">
            <Button variant="outline-primary" type="submit" className="mt-4">
              Entrar
            </Button>
          </div>

          <div className="list-buttons">
            <ul>
              <li>
                <a href="#" onClick={createUser}>
                  Cadastrar usuário
                </a>
              </li>
              <li>
                <a href="#" onClick={activeUser}>
                  Reenvio de Código
                </a>
              </li>
              <li>
                <a href="#" onClick={resendPassword}>
                  Recuperar senha
                </a>
              </li>
            </ul>
          </div>
        </Form>
      </div>
    </DefaultPublic>
  );
};

export default LoginPage;
